import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  baixarPlanilhaCotacaoOnline,
  CanalCotacaoOnline,
  confirmarDesmonteCredenciado,
  cotarRequisicaoOnline,
  fetchCotacaoOnline,
  ItemCotacaoOnline,
  NivelCotacaoOnline,
  OfertaCotacaoOnline,
  rebuscarCotacaoOnline,
} from '../../../api/orcamentoCiliaApi';
import { useAlcanca } from '../../../hooks/usePapeis';
import { useIsTi } from '../../../hooks/useUserType';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/feedback/aviso-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/sub-abas.css';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../recepcao/pages/Recepcao.css';
import '../pages/OrcamentoCiliaDetail.css';
// `.spinner` mora no CSS da lista: importar explicitamente em vez de contar com o bundle (BUG-V74).
import '../pages/OrcamentoCiliaList.css';

export const ROTULO_CANAL: Record<CanalCotacaoOnline, string> = {
  CONCESSIONARIAS: 'Concessionárias',
  MERCADO_LIVRE: 'Mercado Livre',
  FERRO_VELHO: 'Ferro-velho',
  PARALELAS: 'Paralelas',
  LOJAS_AUTOPECAS: 'Lojas de autopeças',
};

/** Consulta pública do DETRAN-RJ — tem captcha, por isso a conferência é humana (decisão de 20/10/2026). */
const CONSULTA_DETRAN = 'https://desmonte.detran.rj.gov.br/apps/desmonte/ConsultaPublicaEmpresasCredenciadas/index';

const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
/** Anúncio sem título legível: o domínio diz ao menos de qual loja é o link. */
const dominioDoLink = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};
const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const ajuda: ComoFuncionaSecao[] = [
  {
    titulo: 'De onde vem o preço',
    conteudo: (
      <p>
        Pelo botão <strong>Cotar peças online</strong>, o sistema procura cada peça a comprar em cinco canais. A inteligência artificial só encontra os
        anúncios e confere se a peça serve no veículo; <strong>o preço é lido da própria página do anúncio</strong> pelo sistema e nunca
        é digitado pela IA. Os preços são unitários e sem frete.
      </p>
    ),
  },
  {
    titulo: 'Cotar peças online',
    conteudo: (
      <p>
        O botão abre a escolha do <strong>nível</strong> (Econômico ou Cuidadoso, que custa mais) e cota as <strong>peças a
        comprar</strong> que ainda não foram pesquisadas. Peça só reparada e peça do CAMINHO VIP (já comprada na rua) ficam de fora.
        Peças que ficaram <strong>sem oferta</strong> entram de novo se você marcar <strong>Recotar</strong>, e a pesquisa nova passa a
        ser o resultado delas; peça que já tem preço e peça em pesquisa não entram. Para refazer uma peça com preço, use o{' '}
        <strong>Rebuscar</strong> dela. A cotação
        não começa mais sozinha no <strong>Concluir e enviar</strong>. Enquanto ela roda, o quadro acima da tabela mostra o que está na
        fila e em que etapa está cada peça: procurando anúncios, lendo as páginas ou conferindo se a peça serve.
      </p>
    ),
  },
  {
    titulo: 'Serve, Conferir e o preço da data',
    conteudo: (
      <p>
        <strong>Conferir</strong> quer dizer que o anúncio provavelmente serve, mas não cita algo (a versão do carro, a cor, o lado).
        O preço aparece mesmo assim, com o que falta conferir. Vale sempre o <strong>último preço pesquisado</strong>, com a data ao
        lado: quem decide se ainda vale é você.
      </p>
    ),
  },
  {
    titulo: 'Rebuscar',
    conteudo: (
      <p>
        Quando o resultado de uma peça não for bom, use <strong>Rebuscar</strong>. O nível <strong>Econômico</strong> é o padrão. O
        nível <strong>Cuidadoso</strong> julga melhor a compatibilidade e custa mais. Toda rebusca fica registrada na auditoria, com quem
        pediu e o nível. Há um teto de gasto mensal: atingido, as pesquisas param até o mês seguinte ou até a administração aumentar o
        teto.
      </p>
    ),
  },
  {
    titulo: 'Mercado Livre e ferro-velho',
    conteudo: (
      <p>
        O Mercado Livre não permite leitura automática; o canal fica vazio até a integração oficial ser configurada. Ofertas de
        ferro-velho aparecem como <strong>credenciamento a conferir</strong>: confira na consulta pública do DETRAN e, se o desmonte
        for credenciado, registre a confirmação. Daí em diante as ofertas dele aparecem como credenciadas.
      </p>
    ),
  },
];

interface Props {
  orcamentoId: string;
  placa: string;
}

const CotacaoOnlineSecao: React.FC<Props> = ({ orcamentoId, placa }) => {
  const isTi = useIsTi();
  const podeRebuscar = useAlcanca(['ORCAMENTO', 'COMPRA']) && !isTi;
  const podeConfirmarDesmonte = useAlcanca(['COMPRA']) && !isTi;
  const [aberto, setAberto] = useState<ItemCotacaoOnline | null>(null);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [baixando, setBaixando] = useState(false);
  // "A comprar" são as peças que o "Cotar peças online" pesquisa; "Fora do fluxo", as de reparo e
  // do CAMINHO VIP (Victor, 21/10/2026): juntas na mesma lista, o reparo se lia como compra esquecida.
  const [subAba, setSubAba] = useState<'A_COMPRAR' | 'FORA_DO_FLUXO'>('A_COMPRAR');

  const consulta = useQuery({
    queryKey: ['orcamento-cilia', 'cotacao-online', orcamentoId],
    queryFn: () => fetchCotacaoOnline(orcamentoId),
    enabled: !!orcamentoId,
    // Enquanto houver pesquisa em curso, a tela se atualiza sozinha.
    refetchInterval: (q) => (q.state.data?.itens.some((i) => i.emCurso) ? 10_000 : false),
  });
  const dados = consulta.data;
  const queryClient = useQueryClient();
  // "Cotar peças online" abre o modal de nível e recotação (Victor, 21/10/2026).
  const [cotarAberto, setCotarAberto] = useState(false);
  const cotar = useMutation({
    mutationFn: (opcoes: { nivel: NivelCotacaoOnline; recotarSemPreco: boolean }) => cotarRequisicaoOnline(orcamentoId, opcoes),
    onSuccess: (r) => {
      setCotarAberto(false);
      notify.success(
        r.enfileiradas === 1 ? 'Uma peça entrou na fila de pesquisa.' : `${r.enfileiradas} peças entraram na fila de pesquisa.`,
      );
      queryClient.invalidateQueries({ queryKey: ['orcamento-cilia', 'cotacao-online', orcamentoId] });
    },
    // A mensagem do servidor diz o motivo (nada a cotar, teto, motor parado): é ela que orienta.
    onError: (e) => notify.errorFrom(e, 'Não foi possível iniciar a cotação online.'),
  });
  const emCurso = dados?.itens.filter((i) => i.emCurso) ?? [];
  const aComprar = dados?.itens.filter((i) => !i.foraDaCotacao) ?? [];
  const foraDoFluxo = dados?.itens.filter((i) => i.foraDaCotacao) ?? [];

  // O modal acompanha a recarga: depois de rebuscar, o item aberto passa a mostrar "em andamento".
  useEffect(() => {
    if (aberto && dados) setAberto(dados.itens.find((i) => i.itemId === aberto.itemId) ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  const baixar = async () => {
    setBaixando(true);
    try {
      const blob = await baixarPlanilhaCotacaoOnline(orcamentoId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cotacao_${placa}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notify.errorFrom(e, 'Não foi possível gerar a planilha da cotação online.');
    } finally {
      setBaixando(false);
    }
  };

  const colunas: ColunaTabela<ItemCotacaoOnline>[] = [
    { titulo: 'Peça', largura: 260, larguraMaxima: 420, texto: (i) => `${i.descricao} (${i.codigoCilia})` },
    {
      // Sem esta coluna, peça de reparo aparecia como "Não pesquisada" e se lia como uma compra que
      // o botão deixou de fazer (Victor, 20/10/2026).
      titulo: 'Tipo',
      largura: 130,
      texto: (i) => ROTULO_TIPO[i.foraDaCotacao ?? 'TROCA'],
      render: (i) => <span className={`cilia-badge ${i.foraDaCotacao ? 'badge-cinza' : 'badge-azul'}`}>{ROTULO_TIPO[i.foraDaCotacao ?? 'TROCA']}</span>,
    },
    { titulo: 'Qtd', largura: 50, texto: (i) => String(i.quantidade) },
    {
      titulo: 'Menor preço',
      largura: 120,
      texto: (i) => (i.resultado?.menorPreco ? moeda(i.resultado.menorPreco.preco) : i.resultado ? 'Sem oferta' : semCompra(i) ? 'Não se aplica' : '-'),
      render: (i) =>
        i.resultado?.menorPreco?.url ? (
          <a href={i.resultado.menorPreco.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {moeda(i.resultado.menorPreco.preco)}
          </a>
        ) : (
          <span>{i.resultado ? 'Sem oferta' : semCompra(i) ? 'Não se aplica' : '-'}</span>
        ),
    },
    { titulo: 'Canal', largura: 140, texto: (i) => (i.resultado?.menorPreco ? ROTULO_CANAL[i.resultado.menorPreco.canal] : semCompra(i) ? 'Não se aplica' : '-') },
    { titulo: 'Pesquisa', largura: 200, texto: textoDaPesquisa },
  ];

  return (
    <div className="cilia-solicitacao-card">
      <div className="solicitacao-card-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Cotação online
          <button className="help-circle-btn" title="Como Funciona" onClick={() => setAjudaAberta(true)}>
            ?
          </button>
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {dados && dados.itens.some((i) => i.resultado) && (
            <button className="link-action" onClick={baixar} disabled={baixando}>
              {baixando ? 'Gerando...' : 'Baixar planilha'}
            </button>
          )}
          {podeRebuscar && dados && (
            <button
              className="cilia-btn primary"
              onClick={() => setCotarAberto(true)}
              disabled={cotar.isPending || !!dados.estado.motivoParado}
              title={dados.estado.motivoParado ?? 'Escolha o nível e quais peças pesquisar'}
            >
              Cotar peças online
            </button>
          )}
          {cotarAberto && (
            <CotarPecasModal
              semResultado={aComprar.filter((i) => !i.resultado && !i.emCurso).length}
              semOferta={aComprar.filter((i) => i.resultado && !i.resultado.menorPreco && !i.emCurso).length}
              emPesquisa={aComprar.filter((i) => i.emCurso).length}
              enviando={cotar.isPending}
              onConfirmar={(o) => cotar.mutate(o)}
              onClose={() => setCotarAberto(false)}
            />
          )}
        </div>
      </div>
      <div>
        {consulta.isError && (
          // 🔴 Falha de carga nunca vira "sem oferta": Compras concluiria que não há preço online.
          <p className="erro-de-tela">
            Não foi possível carregar a cotação online. Recarregue a página; se persistir, acione a TI.
          </p>
        )}
        {dados?.estado.motivoParado && (
          <div className="aviso-de-tela">
            <span className="aviso-de-tela-titulo">Cotação online parada.</span> {dados.estado.motivoParado}
          </div>
        )}
        {emCurso.length > 0 && <AndamentoDaCotacao itens={emCurso} />}
        {dados && !dados.itens.some((i) => i.resultado || i.emCurso || i.ultimaFalha) && (
          <p className="solicitacao-desfecho-hint">
            Nenhuma peça pesquisada ainda. Use <strong>Cotar peças online</strong> para pesquisar todas as peças a comprar; cada peça
            também pode ser pesquisada pelo Rebuscar dela.
          </p>
        )}
        {(dados || consulta.isLoading) && (
          <>
            <div className="sub-abas" role="tablist" aria-label="Peças da cotação online">
              <button
                type="button"
                role="tab"
                aria-selected={subAba === 'A_COMPRAR'}
                className={`sub-aba ${subAba === 'A_COMPRAR' ? 'active' : ''}`}
                onClick={() => setSubAba('A_COMPRAR')}
              >
                A comprar{dados ? ` (${aComprar.length})` : ''}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={subAba === 'FORA_DO_FLUXO'}
                className={`sub-aba ${subAba === 'FORA_DO_FLUXO' ? 'active' : ''}`}
                onClick={() => setSubAba('FORA_DO_FLUXO')}
              >
                Fora do fluxo{dados ? ` (${foraDoFluxo.length})` : ''}
              </button>
            </div>
            {subAba === 'FORA_DO_FLUXO' && (
              <p className="solicitacao-desfecho-hint">
                Peças de reparo (o orçamento não marca troca) e do CAMINHO VIP (já compradas na rua). Não há o que comprar, então o
                Cotar peças online não as pesquisa; se quiser o preço de alguma, abra a peça e use o Rebuscar.
              </p>
            )}
            <TabelaSistema
              colunas={subAba === 'A_COMPRAR' ? colunas.filter((c) => c.titulo !== 'Tipo') : colunas}
              itens={subAba === 'A_COMPRAR' ? aComprar : foraDoFluxo}
              chave={(i) => i.itemId}
              vazio={subAba === 'A_COMPRAR' ? 'Esta requisição não tem peças a comprar.' : 'Nenhuma peça fora do fluxo de compra.'}
              carregando={consulta.isLoading}
              onClickLinha={(i) => setAberto(i)}
            />
          </>
        )}
      </div>

      {aberto && (
        <CanaisDoItemModal
          item={aberto}
          podeRebuscar={podeRebuscar && !dados?.estado.motivoParado}
          podeConfirmarDesmonte={podeConfirmarDesmonte}
          onClose={() => setAberto(null)}
        />
      )}
      <ComoFunciona isOpen={ajudaAberta} onClose={() => setAjudaAberta(false)} titulo="Como Funciona - Cotação online" secoes={ajuda} />
    </div>
  );
};

/**
 * Quadro de acompanhamento: quantas peças estão na fila e em pesquisa, e a etapa de cada uma que
 * está rodando. Some sozinho quando tudo termina (a consulta se atualiza a cada 10 s enquanto há
 * pesquisa em curso).
 */
const AndamentoDaCotacao: React.FC<{ itens: ItemCotacaoOnline[] }> = ({ itens }) => {
  const rodando = itens.filter((i) => i.emCurso?.status === 'EM_ANDAMENTO');
  const naFila = itens.length - rodando.length;
  return (
    <div className="aviso-de-tela" role="status" aria-live="polite" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div className="spinner" style={{ flexShrink: 0 }} />
      <div>
        <span className="aviso-de-tela-titulo">Cotação em andamento</span>{' '}
        {rodando.length} em pesquisa{naFila > 0 ? `, ${naFila} na fila` : ''}. O resultado de cada peça aparece na tabela assim que
        ela termina.
        {rodando.length > 0 && (
          <ul>
            {rodando.map((i) => (
              <li key={i.itemId}>
                <strong>{i.descricao}</strong>: {i.emCurso?.etapaDetalhe ?? 'pesquisando'}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/** O que a peça é no orçamento: é isso que decide se ela entra no "Cotar peças online". */
const ROTULO_TIPO: Record<'TROCA' | 'REPARO' | 'VIP', string> = { TROCA: 'Troca', REPARO: 'Reparo', VIP: 'CAMINHO VIP' };
/** Peça que não vai ser comprada e nunca foi pesquisada: preço e canal não se aplicam. */
const semCompra = (i: ItemCotacaoOnline) => !!i.foraDaCotacao && !i.resultado && !i.emCurso;

export function textoDaPesquisa(i: ItemCotacaoOnline): string {
  if (i.emCurso) return i.emCurso.status === 'PENDENTE' ? 'Na fila' : i.emCurso.etapaDetalhe ?? 'Pesquisando...';
  if (i.ultimaFalha) return `Falhou: ${i.ultimaFalha.erro ?? 'erro na pesquisa'}`;
  if (i.resultado) return `${dataHora(i.resultado.pesquisadaEm)}${i.resultado.nivel === 'CUIDADOSO' ? ' (cuidadosa)' : ''}`;
  if (i.foraDaCotacao) return 'Fora da cotação';
  return 'Não pesquisada';
}

/**
 * Pesquisa em curso: spinner + a etapa que o BACKEND gravou + quanto tempo já passou.
 *
 * ⚠️ A etapa vem do servidor (`etapaDetalhe`), nunca do relógio da tela: inventá-la pelo tempo
 * decorrido seria afirmar o que não se sabe. O "costuma levar" é medido (20/10/2026: 16 a 50 s por peça).
 */
const PesquisaEmAndamento: React.FC<{ emCurso: NonNullable<ItemCotacaoOnline['emCurso']> }> = ({ emCurso }) => {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const segundos = Math.max(0, Math.round((agora - new Date(emCurso.criadaEm).getTime()) / 1000));
  const decorrido = segundos < 60 ? `${segundos} s` : `${Math.floor(segundos / 60)} min ${segundos % 60} s`;
  const naFila = emCurso.status === 'PENDENTE';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0' }} role="status" aria-live="polite">
      <div className="spinner" style={{ flexShrink: 0 }} />
      <div>
        <strong>{naFila ? 'Na fila para pesquisar' : emCurso.etapaDetalhe ?? 'Pesquisando nos cinco canais'}</strong>
        {emCurso.nivel === 'CUIDADOSO' ? ' (nível cuidadoso)' : ''}
        <div className="solicitacao-desfecho-hint">
          {naFila ? 'Começa assim que as pesquisas anteriores terminarem.' : 'Costuma levar menos de um minuto por peça.'}{' '}
          Pedida há {decorrido}. O resultado aparece aqui sozinho.
        </div>
      </div>
    </div>
  );
};

/**
 * O que cada canal trouxe: preço com link, selos, e — no canal sem oferta — cada anúncio aberto com o
 * motivo. Compartilhado pelo modal da requisição e pela aba Cotação online da Auditoria (que é só
 * leitura: sem `onConfirmarDesmonte`, o botão de confirmar credenciamento não aparece).
 */
export const PrecosPorCanal: React.FC<{
  ofertas: OfertaCotacaoOnline[];
  onConfirmarDesmonte?: (o: OfertaCotacaoOnline) => void;
  confirmando?: boolean;
}> = ({ ofertas, onConfirmarDesmonte, confirmando }) => (
  <>
    {ofertas.length > 0 && <h4 style={{ margin: '16px 0 4px' }}>Preços por canal</h4>}
    {ofertas.map((o) => (
      <div key={o.canal} style={{ borderBottom: '1px solid var(--tema-linha, #eee)', padding: '10px 0' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ minWidth: 150 }}>{ROTULO_CANAL[o.canal]}</strong>
          {o.preco != null ? (
            <>
              {o.url ? (
                <a href={o.url} target="_blank" rel="noopener noreferrer">
                  {moeda(o.preco)}
                </a>
              ) : (
                <span>{moeda(o.preco)}</span>
              )}
              {o.kit && <span className="cilia-badge badge-cinza">Kit/par</span>}
              {o.compatibilidade === 'COMPATIVEL' && <span className="cilia-badge badge-verde">Serve</span>}
              {o.compatibilidade === 'CONFERIR' && <span className="cilia-badge badge-laranja">Conferir</span>}
              {o.canal === 'FERRO_VELHO' &&
                (o.credenciado ? (
                  <span className="cilia-badge badge-verde">Credenciado</span>
                ) : (
                  <span className="cilia-badge badge-laranja">Credenciamento a conferir</span>
                ))}
            </>
          ) : (
            <span className="solicitacao-desfecho-hint">{o.semOfertaMotivo ?? 'Sem oferta.'}</span>
          )}
        </div>
        {/*
          Canal sem oferta: QUAIS anúncios foram abertos e por que nenhum serviu, com o link,
          para Compras conferir (Victor, 20/10/2026). Pesquisa anterior a isto não tem a lista.
        */}
        {o.preco == null && (o.anunciosDescartados?.length ?? 0) > 0 && (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {o.anunciosDescartados!.map((a) => (
              <li
                key={a.url}
                className="solicitacao-desfecho-hint"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                title={`${a.titulo ?? a.url} | ${a.motivo}`}
              >
                <a href={a.url} target="_blank" rel="noopener noreferrer">
                  {a.titulo ?? dominioDoLink(a.url)}
                </a>
                {a.preco != null ? ` | ${moeda(a.preco)}` : ''} | {a.motivo}
              </li>
            ))}
          </ul>
        )}
        {o.preco != null && (
          <div className="solicitacao-desfecho-hint" style={{ marginTop: 4 }}>
            {[o.vendedor, o.cidadeUf, o.condicao].filter(Boolean).join(' | ') || 'Vendedor não informado no anúncio'}
            {o.compatibilidade === 'CONFERIR' && o.oQueConferir ? ` | Conferir: ${o.oQueConferir}` : ''}
          </div>
        )}
        {o.canal === 'FERRO_VELHO' && o.preco != null && !o.credenciado && (
          <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a className="link-action" href={CONSULTA_DETRAN} target="_blank" rel="noopener noreferrer">
              Consultar no DETRAN
            </a>
            {onConfirmarDesmonte && (o.cnpj || o.dominio) && (
              <button className="link-action" disabled={confirmando} onClick={() => onConfirmarDesmonte(o)}>
                Confirmar credenciamento
              </button>
            )}
          </div>
        )}
      </div>
    ))}
  </>
);

interface ModalProps {
  item: ItemCotacaoOnline;
  podeRebuscar: boolean;
  podeConfirmarDesmonte: boolean;
  onClose: () => void;
}

const CanaisDoItemModal: React.FC<ModalProps> = ({ item, podeRebuscar, podeConfirmarDesmonte, onClose }) => {
  const queryClient = useQueryClient();
  const [nivel, setNivel] = useState<NivelCotacaoOnline>('ECONOMICO');

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['orcamento-cilia', 'cotacao-online'] });

  const rebuscar = useMutation({
    mutationFn: () => rebuscarCotacaoOnline(item.itemId, nivel),
    onSuccess: () => {
      notify.success('Nova pesquisa pedida. O resultado aparece aqui em alguns segundos.');
      recarregar();
    },
    onError: (e) => notify.errorFrom(e, 'Não foi possível pedir a nova pesquisa.'),
  });

  const confirmar = useMutation({
    mutationFn: (o: OfertaCotacaoOnline) =>
      confirmarDesmonteCredenciado({ nome: o.vendedor || o.dominio || 'Desmonte', cnpj: o.cnpj, dominio: o.dominio }),
    onSuccess: () => {
      notify.success('Credenciamento registrado. As ofertas deste desmonte passam a aparecer como credenciadas.');
      recarregar();
    },
    onError: (e) => notify.errorFrom(e, 'Não foi possível registrar o credenciamento.'),
  });

  const ofertas = item.resultado?.ofertas ?? [];

  return (
    <div className="detail-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="detail-modal-box" style={{ width: 'min(760px, 100%)' }} role="dialog" aria-label="Preços por canal">
        {/* Sem X no cabeçalho: os modais desta tela fecham por "Fechar", clique fora ou Esc. */}
        <div className="detail-modal-header">
          <h3>{item.descricao}</h3>
        </div>
        <div className="detail-modal-body">
          <p className="solicitacao-desfecho-hint">
            Código {item.codigoCilia} | Quantidade {item.quantidade} |{' '}
            {item.emCurso && !item.resultado
              ? 'pesquisa em andamento'
              : item.resultado
              ? `pesquisado em ${dataHora(item.resultado.pesquisadaEm)}${item.resultado.disparadaPorNome ? ` por ${item.resultado.disparadaPorNome}` : ''}`
              : 'ainda não pesquisado'}
          </p>
          {item.emCurso && <PesquisaEmAndamento emCurso={item.emCurso} />}
          {item.ultimaFalha && (
            <p className="erro-de-tela">A última pesquisa falhou: {item.ultimaFalha.erro ?? 'erro na pesquisa'}.</p>
          )}

          {!item.resultado && !item.emCurso && !item.ultimaFalha && item.foraDaCotacao && (
            <p className="group-card-hint">
              {item.foraDaCotacao === 'REPARO'
                ? 'Esta peça é de reparo: o orçamento não marca troca, então não há o que comprar e o Cotar peças online não a pesquisa.'
                : 'Esta peça é do CAMINHO VIP: já foi comprada na rua, então o Cotar peças online não a pesquisa.'}{' '}
              Se mesmo assim quiser o preço, use o Rebuscar abaixo.
            </p>
          )}
          {!item.resultado && !item.emCurso && !item.ultimaFalha && !item.foraDaCotacao && (
            <p className="group-card-hint">
              Esta peça ainda não foi pesquisada. Use <strong>Cotar peças online</strong>, na aba, ou pesquise só esta peça agora, pelo botão
              Rebuscar.
            </p>
          )}

          <PrecosPorCanal
            ofertas={ofertas}
            onConfirmarDesmonte={podeConfirmarDesmonte ? (o) => confirmar.mutate(o) : undefined}
            confirmando={confirmar.isPending}
          />

          {podeRebuscar && !item.emCurso && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: ofertas.length ? '1px solid var(--tema-linha, #f1f5f9)' : undefined }}>
              <h4 style={{ margin: '0 0 4px' }}>{item.resultado ? 'Rebuscar esta peça' : 'Pesquisar esta peça'}</h4>
              <div style={{ display: 'flex', gap: 16, margin: '8px 0', flexWrap: 'wrap' }}>
                <label>
                  <input type="radio" checked={nivel === 'ECONOMICO'} onChange={() => setNivel('ECONOMICO')} /> Econômico
                </label>
                <label>
                  <input type="radio" checked={nivel === 'CUIDADOSO'} onChange={() => setNivel('CUIDADOSO')} /> Cuidadoso
                </label>
              </div>
              {nivel === 'CUIDADOSO' && (
                <div className="aviso-de-tela">
                  O nível cuidadoso julga melhor a compatibilidade e custa mais. A rebusca fica registrada na auditoria, com o seu nome.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="detail-modal-footer">
          <button className="cilia-btn secondary" onClick={onClose}>
            Fechar
          </button>
          {podeRebuscar && !item.emCurso && (
            <button className="cilia-btn primary" disabled={rebuscar.isPending} onClick={() => rebuscar.mutate()}>
              {rebuscar.isPending ? 'Pedindo...' : 'Rebuscar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * "Cotar peças online" (Victor, 21/10/2026): o clique não dispara direto — abre a escolha do nível
 * e se as peças que ficaram SEM OFERTA entram de novo ("só as sem oferta"). Peça que achou preço
 * se refaz pelo Rebuscar dela; peça em pesquisa nunca entra (o servidor a pula), e o modal diz
 * isso em vez de contá-la.
 */
const CotarPecasModal: React.FC<{
  semResultado: number;
  semOferta: number;
  emPesquisa: number;
  enviando: boolean;
  onConfirmar: (o: { nivel: NivelCotacaoOnline; recotarSemPreco: boolean }) => void;
  onClose: () => void;
}> = ({ semResultado, semOferta, emPesquisa, enviando, onConfirmar, onClose }) => {
  const [nivel, setNivel] = useState<NivelCotacaoOnline>('ECONOMICO');
  // Sem peça nova a cotar, o único uso do botão é recotar: a opção já vem marcada.
  const [recotar, setRecotar] = useState(semResultado === 0 && semOferta > 0);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const total = semResultado + (recotar ? semOferta : 0);
  const plural = (n: number, um: string, varios: string) => (n === 1 ? um : varios);

  return (
    <div className="detail-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="detail-modal-box" style={{ width: 'min(520px, 100%)' }} role="dialog" aria-label="Cotar peças online">
        <div className="detail-modal-header">
          <h3>Cotar peças online</h3>
        </div>
        <div className="detail-modal-body">
          <h4 style={{ margin: '0 0 4px' }}>Nível da pesquisa</h4>
          <div style={{ display: 'flex', gap: 16, margin: '8px 0', flexWrap: 'wrap' }}>
            <label>
              <input type="radio" checked={nivel === 'ECONOMICO'} onChange={() => setNivel('ECONOMICO')} /> Econômico
            </label>
            <label>
              <input type="radio" checked={nivel === 'CUIDADOSO'} onChange={() => setNivel('CUIDADOSO')} /> Cuidadoso
            </label>
          </div>
          {nivel === 'CUIDADOSO' && (
            <div className="aviso-de-tela">
              O nível cuidadoso julga melhor a compatibilidade e custa mais, para cada peça pesquisada.
            </div>
          )}

          <h4 style={{ margin: '16px 0 4px' }}>Peças</h4>
          <p className="solicitacao-desfecho-hint" style={{ margin: '0 0 8px' }}>
            {semResultado} {plural(semResultado, 'peça a comprar ainda não pesquisada', 'peças a comprar ainda não pesquisadas')}.
            {emPesquisa > 0 && ` ${emPesquisa} ${plural(emPesquisa, 'está em pesquisa agora e fica de fora', 'estão em pesquisa agora e ficam de fora')}.`}
          </p>
          {semOferta > 0 && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={recotar} onChange={(e) => setRecotar(e.target.checked)} />
              <span>
                Recotar {plural(semOferta, 'a peça que ficou sem oferta', `as ${semOferta} peças que ficaram sem oferta`)}. A pesquisa
                nova passa a ser o resultado delas. Peças que já têm preço não entram; para refazer uma delas, use o Rebuscar.
              </span>
            </label>
          )}
          {total === 0 && (
            <p className="group-card-hint" style={{ marginTop: 8 }}>
              Não há peça a pesquisar com esta escolha.
            </p>
          )}
        </div>
        <div className="detail-modal-footer">
          <button className="cilia-btn secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="cilia-btn primary" disabled={enviando || total === 0} onClick={() => onConfirmar({ nivel, recotarSemPreco: recotar })}>
            {enviando ? 'Iniciando...' : total === 1 ? 'Cotar 1 peça' : `Cotar ${total} peças`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CotacaoOnlineSecao;
